
import { formatInTimeZone } from 'date-fns-tz';
import { TIMEZONE } from "@/utils/dateUtils";

/**
 * Gets today's date in ET timezone format (YYYY-MM-DD)
 */
export function getTodayString(): string {
  return formatInTimeZone(new Date(), TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Gets today's date in UTC format (YYYY-MM-DD)
 * This is used for database storage consistency
 */
export function getTodayUTC(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Gets yesterday's date string in YYYY-MM-DD format (ET timezone)
 */
export function getYesterdayString(): string {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  
  return formatInTimeZone(yesterday, TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Gets a date from 21 days ago in UTC format (YYYY-MM-DD)
 */
export function getTwentyOneDaysAgo(): Date {
  const now = new Date();
  const twentyOneDaysAgo = new Date(now);
  twentyOneDaysAgo.setDate(now.getDate() - 21);
  return twentyOneDaysAgo;
}

/**
 * Gets yesterday's date as a Date object
 */
export function getYesterdayDate(): Date {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return yesterday;
}

/**
 * Format a date for display with weekday, month, day, and year
 */
export function formatDateForDisplay(date: Date): string {
  return formatInTimeZone(date, TIMEZONE, 'EEEE, MMMM d, yyyy');
}

/**
 * Get the next game time (midnight in ET)
 * Returns the Date object representing the next reset time
 */
export function getNextGameTime(): Date {
  // Format the current date in ET timezone to get the current ET date
  const etDateString = formatInTimeZone(new Date(), TIMEZONE, 'yyyy-MM-dd');
  
  // Create midnight ET for today
  const etMidnight = new Date(`${etDateString}T00:00:00.000-04:00`);
  
  // Add 15 minute buffer
  etMidnight.setMinutes(etMidnight.getMinutes() + 15);
  
  // If it's already past midnight ET today, use tomorrow's midnight
  if (new Date() > etMidnight) {
    const tomorrow = new Date(etMidnight);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Debug information
    console.log('Date utils - Next game time is tomorrow with buffer:', {
      nowLocal: new Date().toString(),
      nowET: formatInTimeZone(new Date(), TIMEZONE, 'yyyy-MM-dd HH:mm:ss'),
      tomorrowMidnightET: formatInTimeZone(tomorrow, TIMEZONE, 'yyyy-MM-dd HH:mm:ss'),
      usingTomorrowMidnight: true
    });
    
    return tomorrow;
  }
  
  // Debug information
  console.log('Date utils - Next game time is today with buffer:', {
    nowLocal: new Date().toString(),
    nowET: formatInTimeZone(new Date(), TIMEZONE, 'yyyy-MM-dd HH:mm:ss'),
    todayMidnightET: formatInTimeZone(etMidnight, TIMEZONE, 'yyyy-MM-dd HH:mm:ss'),
    usingTodayMidnight: true
  });
  
  return etMidnight;
}

/**
 * Calculate the time remaining until next puzzle
 * Returns an object with hours and minutes
 */
export function getTimeUntilNextPuzzle(): { hours: number; minutes: number } {
  const now = new Date();
  const nextGame = getNextGameTime();
  
  // Calculate difference in milliseconds
  const diff = nextGame.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { hours: 0, minutes: 0 };
  }
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { hours, minutes };
}

/**
 * Checks if the user has already completed today's puzzle
 * by comparing the saved completion date with today's date (in ET)
 */
export function hasCompletedTodaysPuzzle(): boolean {
  const completedDate = localStorage.getItem("completed_date");
  const todayString = getTodayString();
  
  console.log("hasCompletedTodaysPuzzle check:", {
    completedDate,
    todayString,
    matches: completedDate === todayString,
    currentTimeET: formatInTimeZone(new Date(), TIMEZONE, 'yyyy-MM-dd HH:mm:ss')
  });
  
  return completedDate === todayString;
}
